import {
  SUCCESS_500,
  SUCCESS_600,
  WARNING_500,
  WARNING_700,
  ERROR_500,
  ERROR_700,
  PRIMARY_500,
  PRIMARY_700,
  GRAY_400,
  GRAY_600
} from "../../constant/colors";

/**
 * Shared definitions for the agentic dashboard (`AgenticPlatformDashboard`) —
 * the funnel stage order, the outcome-code → semantic-tone mapping, and the
 * small number formatters. Kept here so the funnel, outcome breakdown, and KPI
 * tiles all read the same source of truth and can't drift apart.
 */

/**
 * The funnel stages, in order, mapping each to its key in the platform's
 * `stats.funnel` object. The funnel counts distinct runs that REACHED each
 * stage (step presence), so it's monotonically non-increasing — every later
 * stage is a subset of the earlier one. Labels come from `agentic_dashboard.funnel.*`.
 *
 * It terminates at "Consent approved" (navigator approved → the agent attempted
 * the send), NOT at "Consent sent": after approval the dispatch is mechanical,
 * and the only things between approved and sent are a send failure (a crash,
 * shown in the failure panel) or a blocked send (shown in the outcome
 * breakdown). The actual sent count is the "Consent sent" KPI tile.
 */
export const FUNNEL_STAGES = Object.freeze([
  { key: "started", labelKey: "agentic_dashboard.funnel.started" },
  { key: "emrCaptured", labelKey: "agentic_dashboard.funnel.emr_captured" },
  { key: "faEligible", labelKey: "agentic_dashboard.funnel.fa_eligible" },
  // "OOP not met" (reached the consent step) is intentionally omitted: it
  // equals "Consent requested" except for already-consented patients (who reach
  // the consent check but short-circuit before the navigator ask), and being
  // requested already implies the OOP gate was cleared. The OOP-met drop folds
  // into the fa_eligible → consent_requested transition and stays in the
  // outcome breakdown (oop_met / patient_has_active_consent).
  { key: "consentRequested", labelKey: "agentic_dashboard.funnel.consent_requested" },
  { key: "approved", labelKey: "agentic_dashboard.funnel.approved" }
]);

/** The in-flight bucket key the platform uses for runs with no terminal yet. */
export const IN_FLIGHT_CODE = "in_flight";

/** Crash terminal (`RxConductorFinalOutcome.FAILURE`). Excluded from the
 *  success-workflow donut and surfaced as its own "Failed" KPI instead. */
export const FAILURE_OUTCOME_CODE = "failure";

/**
 * Semantic tone per `finalOutcome`, used to color the outcome-breakdown
 * bars + the failed panel. Covers the codes the rx-conductor flow produces,
 * plus the `in_flight` synthetic bucket; anything unmapped falls back to "neutral".
 */
export const OUTCOME_TONE = Object.freeze({
  consent_sent_by_navigator: "success",

  in_flight: "info",

  not_eligible: "neutral",
  oop_met: "neutral",
  no_active_medications: "neutral",
  patient_has_active_consent: "neutral",

  consent_send_blocked_missing_info: "warning",
  review_declined: "warning",
  review_timeout: "warning",
  no_screenshots_provided: "warning",

  failure: "error",
  consent_send_failed: "error"
});

/**
 * Codes that are NOT final outcomes — filtered out of the outcome breakdown and
 * the division, which show terminal verdicts only. `in_flight` is the null
 * bucket: a run with no terminal yet.
 */
export const NON_FINAL_OUTCOME_CODES = new Set([IN_FLIGHT_CODE]);

/** Tone → { bar, text } hex pair for the outcome bars and tone dots. */
export const TONE_COLORS = Object.freeze({
  success: { bar: SUCCESS_500, text: SUCCESS_600 },
  info: { bar: PRIMARY_500, text: PRIMARY_700 },
  warning: { bar: WARNING_500, text: WARNING_700 },
  error: { bar: ERROR_500, text: ERROR_700 },
  neutral: { bar: GRAY_400, text: GRAY_600 }
});

/** Tone for an outcome code, defaulting to "neutral" for unknown codes. */
export const outcomeTone = (code) => OUTCOME_TONE[code] || "neutral";

/** Neutral slate for any outcome code without an explicit color. */
export const OUTCOME_COLOR_FALLBACK = "#94A3B8";

/**
 * A distinct color PER outcome code for the success donut — a modern
 * categorical palette (each slice its own hue across the wheel), rather than the
 * shared semantic tones above (which the failed panel still uses). No saturated
 * success-green / warning-orange. Unmapped codes fall back to slate.
 */
export const OUTCOME_COLOR = Object.freeze({
  consent_sent_by_navigator: "#6366F1", // indigo
  not_eligible: "#64748B", // slate
  review_declined: "#EC4899", // pink
  oop_met: "#06B6D4", // cyan
  no_active_medications: "#A855F7", // purple
  patient_has_active_consent: "#F43F5E", // rose
  review_timeout: "#0EA5E9", // sky
  consent_send_blocked_missing_info: "#D946EF", // fuchsia
  consent_send_failed: "#FB7185", // coral
  no_screenshots_provided: "#8B5CF6" // violet
});

/** Distinct per-outcome donut color, defaulting to slate for unknown codes. */
export const outcomeColor = (code) => OUTCOME_COLOR[code] || OUTCOME_COLOR_FALLBACK;

// snake_case → "Title Case" — derived fallback label before a translation lands.
const snakeToTitle = (s) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/**
 * Human label for an outcome code — `agentic_dashboard.outcome.<code>`, with a
 * derived Title Case fallback so a newly-added enum value still renders
 * something readable before its translation lands.
 */
export const outcomeLabel = (code, t) => {
  if (!code) return t("agentic_dashboard.outcome.unknown", "Unknown");
  const fallback = snakeToTitle(code);
  return t(`agentic_dashboard.outcome.${code}`, fallback);
};

/** Whole-number percent of `part` over `whole` (0 when whole is 0). */
export const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/**
 * Human label for the pipeline step a crash happened in (`AgenticStepName`),
 * via `agentic_dashboard.step.<name>` with a Title Case fallback. Null/absent
 * step (crash outside a gate) renders an em dash.
 */
export const stepLabel = (step, t) => {
  if (!step) return "—";
  const fallback = snakeToTitle(step);
  return t(`agentic_dashboard.step.${step}`, fallback);
};
